// =====================================================
// REVISION IDS AND OTHER AUTHORS' REVISIONS across the output package.
// =====================================================

import { decideRevisions } from './decide-revisions';
import { recLabelOf } from './change-record';
import { balanceCommentAnchors } from './anchors';

/**
 * The revision ids of the output package, in one space:
 *
 * - The drafts' own revisions that the output keeps (Combine) keep their ids.
 * - Fredline's revisions in notes, comments, headers and footers take ids from the writer's counter,
 *   which starts above every revision id either draft uses in any part (startWriting), so none can
 *   take an id a kept revision has.
 * - The body's revisions (the common kinds) are numbered last, in document order, from 1,000,000,
 *   or from above everything the counter has given out when a draft already uses ids that high (a
 *   Fredline redline compared again). The counter then moves past them.
 *
 * Duplicate revision ids can make Word treat unrelated edits as one revision, or flag the file.
 */
export const REV_ID_RE = /(<w:(?:ins|del|moveFrom|moveTo|pPrChange|rPrChange|sectPrChange|tblPrChange|trPrChange|tcPrChange|tblGridChange|tblPrExChange|cellIns|cellDel|cellMerge|numberingChange)\b[^>]*?\bw:id=")(\d+)(")/g;

/** The highest revision id in the drafts' notes, comments, headers and footers (their bodies' ids
 *  are read by startWriting). */
export async function maxRevisionIdOutsideBody(...zips: any[]): Promise<number> {
  let max = 0;
  for (const zip of zips) {
    if (!zip) continue;
    for (const n of Object.keys(zip.files).filter((n: string) => /^word\/(footnotes|endnotes|comments|header\d*|footer\d*)\.xml$/.test(n))) {
      const x: string = await zip.file(n).async('string');
      for (const m of x.matchAll(REV_ID_RE)) max = Math.max(max, Number(m[2]));
    }
  }
  return max;
}

/** The body's revisions numbered from `base` in document order; `next` is the first id after them. */
export function numberBodyRevisions(xml: string, base: number): { xml: string; next: number } {
  let n = base;
  // (a revision this run wrote is labelled with its change, for the change record: change-record.ts)
  const out = xml.replace(
    /(<w:(?:ins|del|pPrChange|rPrChange|moveFrom|moveTo)\b[^>]*?\bw:id=")(\d+)(")/g,
    (_m, pre, id, post) => {
      const c = recLabelOf(Number(id));
      return pre + (n++) + post + (c != null ? ` fl-c="${c}"` : '');
    },
  );
  return { xml: out, next: n };
}

/**
 * Compare (and markup) output carries Fredline's revisions only. The body is built from the
 * drafts' accepted text; notes, comments, headers and footers are copied or rebuilt from the
 * revised draft and could keep its tracked changes. Accept every revision in them that this run
 * did not make (its author and date). A part without such revisions is left as it was.
 */
export const FOREIGN_REV_RE = /<w:(?:ins|del|moveFrom|moveTo|pPrChange|rPrChange|sectPrChange|tblPrChange|trPrChange|tcPrChange|tblGridChange|tblPrExChange|cellIns|cellDel|cellMerge)\b[^>]*>/g;
export async function acceptForeignRevisionsInParts(zip: any, author: string, date: string, keepOurs: boolean): Promise<void> {
  const ours = (a: string | null, d: string | null) => keepOurs && a === author && d === date;
  const attr = (tag: string, n: string) => { const m = tag.match(new RegExp(`\\b${n}="([^"]*)"`)); return m ? m[1].replace(/&quot;/g, '"').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&amp;/g, '&') : null; };
  for (const n of Object.keys(zip.files).filter(n => /^word\/(footnotes|endnotes|comments|header\d*|footer\d*)\.xml$/.test(n)).sort()) {
    const xml: string = await zip.file(n).async('string');
    if (![...xml.matchAll(FOREIGN_REV_RE)].some(m => !ours(attr(m[0], 'w:author'), attr(m[0], 'w:date')))) continue;
    const doc = new DOMParser().parseFromString(xml, 'application/xml');
    decideRevisions(doc, (_id, el) => ours(el.getAttribute('w:author'), el.getAttribute('w:date')) ? null : 'accept');
    let out = new XMLSerializer().serializeToString(doc).replace(/^<\?xml[^>]*\?>\s*/, '');
    if (n !== 'word/comments.xml') out = balanceCommentAnchors(out);
    // the part's own XML declaration and line break, as it was
    zip.file(n, ((xml.match(/^<\?xml[^>]*\?>\s*/) || [])[0] ?? '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\r\n') + out);
  }
}
